import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { authAPI, prescriptionAPI } from '../../../utils/api';
import AccountSettingsModal from '../../../components/AccountSettingsModal';

const Profile = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    bio: '',
    health_profile: {
      blood_group: '',
      allergies: [],
      chronic_conditions: [],
      current_medications: [],
      vitals: { height: '', weight: '', bp_systolic: '', bp_diastolic: '', temp: '', spo2: '' }
    }
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        bio: user.bio || '',
        health_profile: user.health_profile || {
          blood_group: '',
          allergies: [],
          chronic_conditions: [],
          current_medications: [],
          vitals: { height: '', weight: '', bp_systolic: '', bp_diastolic: '', temp: '', spo2: '' }
        }
      });
      fetchPrescriptions();
    }
  }, [user]);

  const fetchPrescriptions = async () => {
    try {
      const res = await prescriptionAPI.getPatientPrescriptions({ limit: 5 });
      if (res.data?.success) {
        setPrescriptions(res.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch prescriptions", error);
    }
  };

  const handleInputChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const updateProfile = async () => {
    try {
      const res = await authAPI.updateProfile(profileData);
      if (res.data?.success) {
        showSuccess('Profile updated successfully');
        await refreshUser(); // Update global auth state
        setIsEditing(false);
      } else {
        showError(res.data?.message || 'Failed to update profile');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">My Profile</h1>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
        >
          {isEditing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center mb-6">
          <div className="w-20 h-20 bg-gold rounded-full flex items-center justify-center mr-4">
            <span className="text-2xl font-bold text-dark">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <h2 className="text-xl font-semibold">{user?.name || 'User'}</h2>
            <p className="text-gray-400 capitalize">{user?.role === 'patient' ? 'User' : user?.role}</p>
            {user?.subscription_status === 'active' && (
              <span className="bg-green-900 text-green-300 px-2 py-1 rounded-full text-xs mt-1 inline-block">
                Active Subscription
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-gray-400 mb-2">Full Name</label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={profileData.name}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.name}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Email</label>
            <p className="text-light">{profileData.email}</p>
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Phone</label>
            {isEditing ? (
              <input
                type="tel"
                name="phone"
                value={profileData.phone}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.phone || 'Not provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Address</label>
            {isEditing ? (
              <textarea
                name="address"
                value={profileData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.address || 'Not provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Bio</label>
            {isEditing ? (
              <textarea
                name="bio"
                value={profileData.bio}
                onChange={handleInputChange}
                rows="3"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.bio || 'No bio provided'}</p>
            )}
          </div>

          <hr className="border-gray-700 my-4" />
          <h3 className="text-lg font-semibold text-gold mb-4">Detailed Information</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 mb-2">Blood Group</label>
              {isEditing ? (
                <select
                  name="blood_group"
                  value={profileData.health_profile.blood_group}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    health_profile: { ...profileData.health_profile, blood_group: e.target.value }
                  })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                >
                  <option value="">Select</option>
                  {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              ) : (
                <p className="text-light">{profileData.health_profile.blood_group || 'Not set'}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Allergies (comma-separated)</label>
            {isEditing ? (
              <input
                type="text"
                placeholder="e.g. Peanuts, Penicillin"
                value={profileData.health_profile.allergies.join(', ')}
                onChange={(e) => setProfileData({
                  ...profileData,
                  health_profile: { ...profileData.health_profile, allergies: e.target.value.split(',').map(s => s.trim()) }
                })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.health_profile.allergies.join(', ') || 'None reported'}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-400 mb-2">Height (cm)</label>
              {isEditing ? (
                <input
                  type="number"
                  value={profileData.health_profile.vitals.height}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    health_profile: {
                      ...profileData.health_profile,
                      vitals: { ...profileData.health_profile.vitals, height: e.target.value }
                    }
                  })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light"
                />
              ) : (
                <p className="text-light">{profileData.health_profile.vitals.height || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-gray-400 mb-2">Weight (kg)</label>
              {isEditing ? (
                <input
                  type="number"
                  value={profileData.health_profile.vitals.weight}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    health_profile: {
                      ...profileData.health_profile,
                      vitals: { ...profileData.health_profile.vitals, weight: e.target.value }
                    }
                  })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light"
                />
              ) : (
                <p className="text-light">{profileData.health_profile.vitals.weight || '-'}</p>
              )}
            </div>
          </div>
        </div>

        {isEditing && (
          <button
            onClick={updateProfile}
            className="w-full bg-gold text-dark py-3 rounded-lg font-medium mt-6"
          >
            Save Changes
          </button>
        )}
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gold mb-4">Recommendation History</h3>
        {prescriptions.length === 0 ? (
          <p className="text-gray-400">No recommendations found.</p>
        ) : (
          <div className="space-y-3">
            {prescriptions.map((pres) => (
              <div key={pres._id} className="flex justify-between items-center bg-gray-700/50 p-3 rounded-lg border border-gray-600 hover:border-gold transition-colors">
                <div className="flex items-center gap-3">
                  <div className="bg-gray-800 p-2 rounded-full">
                    <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{pres.doctorDetails?.name || 'Provider'}</p>
                    <p className="text-xs text-gray-400">{new Date(pres.createdAt).toLocaleDateString()} • {pres.doctorDetails?.specialization}</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/patient/prescription/${pres.consultation_id}`)}
                  className="bg-gold text-dark px-3 py-1.5 rounded text-sm font-bold hover:bg-yellow-500"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Support & Legal</h3>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/terms-and-conditions')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Terms & Conditions
          </button>
          <button
            onClick={() => navigate('/privacy-policy')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Privacy Policy
          </button>
          <button
            onClick={() => navigate('/about-us')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            About Us
          </button>
          <button
            onClick={() => navigate('/patient/support')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex justify-between items-center"
          >
            <span>Contact Support</span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </button>
        </div>
      </div>


      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Account Settings</h3>
        <div className="space-y-3">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex justify-between items-center"
          >
            <span>Change Password / Delete Account</span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={handleLogout}
            className="w-full text-left py-3 px-4 bg-red-900 text-red-300 rounded-lg hover:bg-red-800 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>

      <AccountSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
    </div>
  );
};

export default Profile;
